/**
 * Desktop Client Fragment
 * Keeps the desktop client settings enabled or disabled to match the choices
 * above them: letting users change settings locks the whole set, and each
 * optional feature governs its own fields.
 */

import { Events } from '../../../../js/utils/events.js';

let initialized = false;

// Every field the "allow users to change settings" checkbox locks.
const MANAGED_FIELDS = [
    'enable_automatic_update', 'chunk_size', 'number_of_streams', 'number_of_retries',
    'use_proxy', 'proxy_address', 'proxy_port',
    'use_threshold', 'threshold_mb',
    'enable_domain_exclusion', 'excluded_domains',
    'show_confirmation_dialog', 'hide_send_button',
    'save_sent_attachments',
];

// ============================================================================
// Desktop Client Handlers
// Template contract: <div data-desktop-client> around the settings sections.
// Fields are addressed by the name the backend reads, not by id.
// ============================================================================

function field(container, name) {
    return container.querySelector('[name="' + name + '"]');
}

function updateFieldStates(container) {
    const allowUsersChange = field(container, 'allow_users_change_settings');
    if (!allowUsersChange) return;
    const locked = allowUsersChange.checked;

    MANAGED_FIELDS.forEach((name) => {
        const el = field(container, name);
        if (el) el.disabled = locked;
    });
    container.querySelectorAll('[name="transfer_method"]').forEach((radio) => {
        radio.disabled = locked;
    });

    if (locked) return;

    // Left free, each optional feature still governs its own fields.
    const useProxy = field(container, 'use_proxy');
    const proxyAddress = field(container, 'proxy_address');
    const proxyPort = field(container, 'proxy_port');
    if (proxyAddress) proxyAddress.disabled = !(useProxy && useProxy.checked);
    if (proxyPort) proxyPort.disabled = !(useProxy && useProxy.checked);

    const useThreshold = field(container, 'use_threshold');
    const thresholdField = field(container, 'threshold_mb');
    if (thresholdField && useThreshold) thresholdField.disabled = !useThreshold.checked;

    const enableDomainExclusion = field(container, 'enable_domain_exclusion');
    const domainsField = field(container, 'excluded_domains');
    if (domainsField && enableDomainExclusion) {
        domainsField.disabled = !enableDomainExclusion.checked;
    }

    const attachmentsOnly = container.querySelector('[name="transfer_method"][value="attachments_only"]');
    const saveSentAttachments = field(container, 'save_sent_attachments');
    if (saveSentAttachments && attachmentsOnly) {
        saveSentAttachments.disabled = attachmentsOnly.checked;
    }
}

// ============================================================================
// Initialization
// ============================================================================

export function initDesktopClient() {
    if (initialized) return;
    initialized = true;

    Events.on('change', 'desktop-client', (event, container) => {
        updateFieldStates(container);
    });

    // Cancel reverts field values synchronously, so re-read them after.
    Events.on('click', 'dirty-cancel', (event, element) => {
        const container = element.closest('[data-desktop-client]');
        if (container) setTimeout(() => updateFieldStates(container), 0);
    });

    document.querySelectorAll('[data-desktop-client]').forEach(updateFieldStates);

    // The container can land on either side of the swap target, so both
    // directions have to be searched:
    //   arriving from the settings nav — hx-target="#sidebar-content"
    //   hx-swap="innerHTML" — puts it below event.target;
    //   saving a section — hx-swap="outerHTML" on a form inside it — puts
    //   event.target below the container.
    // Scoped off event.target throughout: detail.target is the replaced
    // element and is already detached, which is why the old
    // container.contains(detail.target) test never matched after a save.
    document.addEventListener('htmx:afterSwap', (event) => {
        const swapped = event.target;
        if (!swapped.closest) return;
        const above = swapped.closest('[data-desktop-client]');
        if (above) {
            updateFieldStates(above);
            return;
        }
        swapped.querySelectorAll('[data-desktop-client]').forEach(updateFieldStates);
    });
}
